import { createContext, useEffect, useRef, useState } from 'react'
import jwt_decode from 'jwt-decode'
import * as queryString from 'query-string'
import { useHistory } from 'react-router'
import {
  HOME_PATH,
  NEW_SEO,
  SELECTION_STEP_PATH_LOADED,
  SIGN_IN_PATH
} from 'routes/constants.routes'
import { AuthService, SalesService, SeoService } from 'lib/api'

import { useResErrors, useRoutes } from 'hooks'
import { deepCopyObject, LocalStorageService } from 'lib/utils'
import { useModalbar } from 'modules/Modalbar'
import { useTranslation } from 'react-i18next'
import { LoaderBackdrop } from 'asterX-UI/Loaders'
import { ACCESS_TO_PROJECT, MANAGER, PROJECT_CODE_V2 } from 'constants/roles'
import { useSnackbar } from 'notistack'
import { getPrevStepByStatus } from 'constants/deals'

const initialState = {
  token: '',
  role: null,
  branchList: [],
  data: {}
}

export const AuthStoreContext = createContext({
  authStore: {},
  authStoreFunc: {}
})
const AuthStoreProvider = props => {
  let currentPathname
  let currentSearch
  const { location } = props
  const { t } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()
  const { push, lastVisit, goTo } = useRoutes()
  const { standardCheck, silentCheck } = useResErrors()
  const { callModalbar } = useModalbar()
  const _isMounted = useRef(true)
  const history = useHistory()
  const [appBar, setAppBar] = useState(true)
  const [user, setUser] = useState(initialState)
  const [isLoggedIn, setIsLoggedIn] = useState(false)
  const [loading, setLoading] = useState(false)
  const [initLoading, setInitLoading] = useState(false)
  const [seoToken, setSeoToken] = useState(null)
  const [seoDepartments, setSeoDepartments] = useState(null)
  useEffect(() => {
    initEffect()

    return () => {
      _isMounted.current = false
    }
  }, [])
  const redirect = () => {
    const newSeoId = LocalStorageService.getSelectionId()
    if (newSeoId) {
      return SELECTION_STEP_PATH_LOADED(newSeoId)[NEW_SEO]
    }
    if (lastVisit && lastVisit !== SIGN_IN_PATH) {
      return lastVisit
    }
    return HOME_PATH
  }
  const initEffect = async () => {
    setInitLoading(true)
    const values = queryString.parse(location?.search)
    if (values && values.token) {
      await setTokenFromQuery(values.token)
      setAppBar(false)
    }
    if (user.token) {
      await setTokenFromQuery(user.token)
    } else {
      await setTokenFromLS()
    }
  }

  const logout = () => {
    AuthService.setToken(null)
    setUser(deepCopyObject(initialState))
    setIsLoggedIn(false)
    setSeoToken(null)
    history.push(SIGN_IN_PATH)
  }

  const isThisProject = user =>
    !!user?.roleList?.some(el => [PROJECT_CODE_V2].includes(el?.project?.code))

  const checkRoleOfUser = (user, key = 'roleList') => {
    return user?.[key]?.find(
      el =>
        [PROJECT_CODE_V2].includes(el?.project?.code) &&
        ACCESS_TO_PROJECT.includes(el.code)
    )
  }

  const setDataToStorage = async (token = '', isReset = false) => {
    AuthService.setToken(token)
    let role = null

    const lastLogin = LocalStorageService.getLastLogin()

    if (isReset) {
      setUser(deepCopyObject(initialState))
      setIsLoggedIn(false)
      return
    }

    setLoading(true)
    await AuthService.getUserByToken().then(res =>
      standardCheck(
        res,
        async () => {
          role = checkRoleOfUser(res.data, 'roles')
          LocalStorageService.setLastLogin(res.data.username)

          setUser(prevState => ({
            ...prevState,
            token,
            data: res.data,
            branchList: role?.branchList,
            role: role?.code
          }))
          if (lastLogin !== null && lastLogin !== res.data.username) {
            removeSelectionId()
            LocalStorageService.removeLastLogin()
          }

          setIsLoggedIn(!!token && !!res?.data?.id)
        },
        {
          onFail: () => {
            role = null
            setIsLoggedIn(false)
            AuthService.setToken()
            history.push(SIGN_IN_PATH)
          }
        }
      )
    )
    if (role?.code === MANAGER) {
      await authManagerInSeo()
    }

    setLoading(false)
    return role?.code
  }

  const authManagerInSeo = async () => {
    await SeoService.getUser().then(seo =>
      standardCheck(seo, () => {
        setSeoToken(seo.data?.token)
        enqueueSnackbar('Успешная авторизация в СЭО', {
          variant: 'success',
          autoHideDuration: 600
        })
        SeoService.getDepartments().then(resD =>
          standardCheck(resD, () => {
            const derpartments = Object.keys(seo.data?.Redirect || {})
            if (resD.data && resD.data.length > 0) {
              const newDeps = resD.data.reduce(
                (prev, group) => [...prev, ...group.Departments],
                []
              )
              const deps = newDeps.filter(({ Id }) =>
                derpartments.includes('d' + Id)
              )
              LocalStorageService.setSeoDepartments(deps)
              return setSeoDepartments(deps)
            }
            setSeoDepartments([])
          })
        )
      })
    )
  }

  const setTokenFromQuery = async token => {
    if (!token) {
      setInitLoading(false)
      goTo(SIGN_IN_PATH)
      return false
    }
    try {
      const decoded = jwt_decode(token)
      if (decoded && isThisProject(decoded) && checkRoleOfUser(decoded)) {
        const role = await setDataToStorage(token)
        setInitLoading(false)

        if (role === MANAGER) {
          return returnToSelection()
        }
        push(redirect())
        //  history.push(HOME_PATH)

        return true
      } else {
        await setDataToStorage(null, true)
        setInitLoading(false)
        return false
      }
    } catch (e) {
      setInitLoading(false)
      return false
    }
  }

  const setTokenFromLS = () =>
    setTokenFromQuery(LocalStorageService.getAccessToken())

  const returnToSelection = async () => {
    setLoading(true)

    await SalesService.getActiveSales().then(res =>
      silentCheck(
        res,
        () => {
          if (res.data?.id) {
            const sale = res.data
            const step = getPrevStepByStatus(
              sale?.saleStatus?.code,
              sale?.saleStatusTrack?.trackStatus?.code
            )

            push(
              SELECTION_STEP_PATH_LOADED(sale.id)[
                res.data?.isNewSeo ? NEW_SEO : step
              ]
            )
          }
        },
        {
          onFail: () => {
            push(redirect())
            //push(HOME_PATH)
          }
        }
      )
    )
    setLoading(false)
  }
  const removeSelectionId = async () => {
    await LocalStorageService.removeSelectionId()
    push(HOME_PATH)
  }

  const login = async userData => {
    setLoading(true)
    await AuthService.login(userData).then(res =>
      standardCheck(
        res,
        async () => {
          setLoading(false)
          const { token } = res.data
          const decoded = jwt_decode(token)
          if (
            !!token &&
            !!decoded &&
            isThisProject(decoded) &&
            checkRoleOfUser(decoded)
          ) {
            const role = await setDataToStorage(token)
            if (role === MANAGER) {
              return returnToSelection()
            }
            push(redirect())
          } else {
            callModalbar(
              t('errors.NotAccess_SignIn.title'),
              t('errors.NotAccess_SignIn.body'),
              { btnText: 'OK' }
            )
          }
        },
        {
          onErrCustom: () => {
            setLoading(false)
            callModalbar(
              t('errors.Unauthorized_SignIn.title'),
              t('errors.Unauthorized_SignIn.body'),
              { btnText: 'OK' }
            )
          }
        }
      )
    )
  }

  useEffect(() => {
    history.listen((newLocation, action) => {
      if (action === 'PUSH') {
        if (
          newLocation.pathname !== currentPathname ||
          newLocation.search !== currentSearch
        ) {
          // Save new location
          currentPathname = newLocation.pathname
          currentSearch = newLocation.search

          // Clone location object and push it to history
          history.push({
            pathname: newLocation.pathname,
            search: newLocation.search
          })
        }
      } else {
        // Send user back if they try to navigate back
        history.go(1)
      }
    })
    return () => {
      _isMounted.current = false
    }
  }, [history])

  const authStore = {
    seoToken,
    seoDepartments,
    appBar,
    user,
    isLoggedIn,
    loading
  }

  const authStoreFunc = {
    login,
    setLoading,
    logout,
    setTokenFromQuery,
    setTokenFromLS,
    authManagerInSeo
  }

  return (
    <AuthStoreContext.Provider value={{ authStore, authStoreFunc }}>
      {initLoading ? (
        <LoaderBackdrop loading={initLoading} />
      ) : (
        <>{props.children}</>
      )}
    </AuthStoreContext.Provider>
  )
}
export default AuthStoreProvider
